import { Clear, ModeEdit, SaveAs } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { finalize } from "rxjs";
import { accountInformationService } from "services/accountInformationService";
import { notifications$ } from "services/subjects";
import TextInput from "./forms/TextInput/TextInput";
import PhoneInput from "./forms/PhoneInput/PhoneInput";
import { isValidPhone } from "utils/validations";

type ContactInformationFormValues = {
  homePhone: string;
  cellPhone: string;
  email: string;
};

type ContactInformationFormProps = {
  homePhone?: string;
  cellPhone?: string;
  email?: string;
  memberId?: string;
  readOnly?: boolean;
  initialLoading?: boolean;
  onUpdateSuccess?: () => void;
};

const ContactInformationForm: React.FC<ContactInformationFormProps> = (
  props
) => {
  const [saveEnabled, setSaveEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  const { control, handleSubmit, reset } =
    useForm<ContactInformationFormValues>({
      defaultValues: {
        homePhone: props.homePhone ?? "",
        cellPhone: props.cellPhone ?? "",
        email: props.email ?? "",
      },
    });

  useEffect(() => {
    if (props.homePhone || props.cellPhone || props.email) {
      reset({
        homePhone: props.homePhone ?? "",
        cellPhone: props.cellPhone ?? "",
        email: props.email ?? "",
      });
      setSaveEnabled(false);
    }
  }, [props.homePhone, props.cellPhone, props.email]);
  

  const initialLoading = props.initialLoading;

  const handleSubmitForm = (data: ContactInformationFormValues) => {
    const finalForm = {
      homePhone: data.homePhone.trim() || null,
      cellPhone: data.cellPhone.trim() || null,
      email: data.email.trim() || null,
      memberId: props.memberId,
    };
    setSaving(true);
    accountInformationService
      .updateContactInformation$(finalForm)
      .pipe(finalize(() => setSaving(false)))
      .subscribe({
        next: () => {
          notifications$.next({
            severity: "success",
            message:
              "Done! - Your request has been submitted",
          });
          setSaveEnabled(false);
          if (props.onUpdateSuccess) {
            props.onUpdateSuccess();
          }
        },
        error: () => {
          notifications$.next({
            severity: "error",
            message:
              "An error occurred while saving your contact information. Please try again.",
          });
        },
      });
  };

  const disabled = props.readOnly || !saveEnabled || saving;

  return (
    <Box mb={4}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="h4">Contact Information</Typography>
        {saving ? (
          <CircularProgress size={24} />
        ) : saveEnabled ? (
          <Box>
            <Button
              variant="text"
              aria-label="save"
              onClick={handleSubmit(handleSubmitForm)}
            >
              <SaveAs fontSize="small" sx={{ mr: 1 }} color="primary" />
              <Typography color="primary" fontSize="small">
                Save changes
              </Typography>
            </Button>
            <Button
              variant="text"
              aria-label="cancel"
              onClick={() => setSaveEnabled(false)}
            >
              <Clear color="error" sx={{ mr: 1 }} />
              <Typography fontSize="small" color="error">
                Clear
              </Typography>
            </Button>
          </Box>
        ) : (
          <Button
            variant="text"
            disabled={props.readOnly}
            aria-label="update contact info"
            onClick={() => setSaveEnabled(true)}
          >
            <ModeEdit fontSize="small" sx={{ mr: 1 }} />
            <Typography fontSize="small">Edit</Typography>
          </Button>
        )}
      </Box>
      <Grid
        container
        spacing={2}
        columns={{
          xs: 1,
          sm: 3,
          md: 5,
        }}
      >
        <Grid size={1}>
          <Controller
            name="homePhone"
            control={control}
            rules={{
              required: "Phone number is required",
              validate: (val) =>
                isValidPhone(val ?? "") || "Please provide a valid US phone number",
            }}
            render={({ field, fieldState: { error } }) => (
              <PhoneInput
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                ref={field.ref}
                label="Home Phone Number"
                fullWidth
                disabled={disabled}
                initialLoading={initialLoading}
                error={!!error?.message}
                helperText={error?.message || ""}
              />
            )}
          />
        </Grid>
        <Grid size={1}>
          <Controller
            name="cellPhone"
            control={control}
            rules={{
              validate: (val) =>
                !val || isValidPhone(val) || "Please provide a valid US phone number",
            }}
            render={({ field, fieldState: { error } }) => (
              <PhoneInput
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                ref={field.ref}
                label="Mobile Phone Number"
                fullWidth
                disabled={disabled}
                initialLoading={initialLoading}
                error={!!error?.message}
                helperText={error?.message || ""}
              />
            )}
          />
        </Grid>
        <Grid size={1}>
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Email address is required",
            }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label="Email Address"
                fullWidth
                disabled={disabled}
                error={!!error?.message}
                helperText={error?.message || ""}
                initialLoading={initialLoading}
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContactInformationForm;
